import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Loading from './Loading';
import ErrorBoundary from './ErrorBoundary.js';
import 'github-markdown-css';
const Wrapper = styled.div`
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  padding: 10px;
  @media print {
    padding: 0;
    overflow: visible;
  }
`;
const MissingImageNotice = styled.div`
  margin-bottom: 12px;
  padding: 8px 12px;
  color: #7c2d12;
  background: #ffedd5;
  border: 1px solid #fdba74;
  border-radius: 4px;
  font-size: 14px;
`;
const LazyPreview = lazy(() => import('./Preview.js'));
export default ({ source, children }) => {
  const previewRef = useRef(null);
  const [missingImageSources, setMissingImageSources] = useState([]);
  const previewSource = source === undefined ? children : source;

  useEffect(() => {
    setMissingImageSources([]);
    const preview = previewRef.current;
    if (!preview) return undefined;

    let disposed = false;
    const observedImages = new WeakSet();
    const imageListeners = new Map();
    const markMissingImage = image => {
      if (disposed) return;
      const source = image.getAttribute('src') || image.getAttribute('alt') || 'unknown image';
      setMissingImageSources(previous =>
        previous.includes(source) ? previous : [...previous, source]
      );
    };
    const observeImages = () => {
      Array.from(preview.querySelectorAll('img')).forEach(image => {
        if (image.complete && image.naturalWidth === 0) markMissingImage(image);
        if (observedImages.has(image)) return;
        observedImages.add(image);
        const listener = () => markMissingImage(image);
        imageListeners.set(image, listener);
        image.addEventListener('error', listener);
      });
    };
    const observer = new MutationObserver(observeImages);
    observer.observe(preview, { childList: true, subtree: true });
    observeImages();

    return () => {
      disposed = true;
      observer.disconnect();
      imageListeners.forEach((listener, image) => image.removeEventListener('error', listener));
    };
  }, [previewSource]);

  return (
    <ErrorBoundary>
      <Suspense fallback={<Loading duration={0.5} />}>
        <Wrapper ref={previewRef} className="preview  markdown-body">
          {missingImageSources.length > 0 && (
            <MissingImageNotice className="no-print" role="alert">
              图片加载失败：{missingImageSources.join(', ')}。请同时选择 Markdown 文件和本地图片，或将图片拖入页面。<br />
              Image failed to load: {missingImageSources.join(', ')}. Select the Markdown file and its local images together, or drop the image into the page.
            </MissingImageNotice>
          )}
          <LazyPreview source={previewSource}>{children}</LazyPreview>
        </Wrapper>
      </Suspense>
    </ErrorBoundary>
  );
};
